/**
 * useLivePreviewCapture — periodically captures a 5-second clip from the
 * broadcaster's local <video> and uploads it to /api/live/{id}/preview so
 * the homepage discovery rail can play a silent looping MP4 on hover.
 *
 * Mounted by `pages/DirectorsMode.jsx` once the stream is live.
 *
 * Usage:
 *   useLivePreviewCapture({ streamId, isLive, intervalSec: 60 });
 */
import { useEffect, useRef } from "react";
import api from "../lib/api";

export default function useLivePreviewCapture({ streamId, isLive, intervalSec = 60 }) {
  const recRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!streamId || !isLive) return;
    let stopped = false;

    const captureOnce = async () => {
      const v = document.querySelector('[data-testid="live-broadcaster-preview"] video') ||
                document.querySelector('video');
      if (!v || !v.captureStream || v.readyState < 2) return;
      try {
        const stream = v.captureStream();
        const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
          ? "video/webm;codecs=vp9,opus" : "video/webm";
        const rec = new MediaRecorder(stream, { mimeType: mime });
        recRef.current = rec;
        const chunks = [];
        const stoppedP = new Promise((r) => { rec.onstop = () => r(); });
        rec.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
        rec.start();
        await new Promise((r) => setTimeout(r, 5000));
        if (stopped) { try { rec.stop(); } catch (_) {} return; }
        try { rec.stop(); } catch (_) {}
        await stoppedP;
        const blob = new Blob(chunks, { type: "video/webm" });
        if (blob.size < 50_000) return;     // ignore truly empty captures

        const fd = new FormData();
        fd.append("file", new File([blob], `preview-${streamId}.webm`, { type: "video/webm" }));
        await api.post(`/live/${streamId}/preview`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (_) { /* swallow — best-effort */ }
    };

    // Kick off after 30s so the broadcaster is settled, then every interval
    timerRef.current = setTimeout(function tick() {
      captureOnce();
      timerRef.current = setTimeout(tick, intervalSec * 1000);
    }, 30_000);

    return () => {
      stopped = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      try { if (recRef.current?.state === "recording") recRef.current.stop(); } catch (_) {}
    };
  }, [streamId, isLive, intervalSec]);
}
